// React
import { useTranslation } from 'react-i18next';
import { useLocation, Link, useMatches, type UIMatch } from 'react-router-dom';

import cn from '@/utils/cn';
import { buildLocalizedPath, getLocale } from '@/utils/localeSlug';
import { stripTrailingSlash, withTrailingSlash } from '@/utils/trailingSlash';

// Styles
import styles from './Breadcrumb.module.css';

interface BreadcrumbProps {
  color?: 'dark' | 'light';
}

function Breadcrumb({
  color = 'dark',
}: BreadcrumbProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const matches = useMatches() as UIMatch[];
  
  const locale = getLocale(pathname);

  const breadcrumbMatches = matches
    .map(match =>
      stripTrailingSlash(match.pathname) === '/offres-et-services'
        ? { ...match, handle: { breadcrumb: t('offres-et-services') } }
        : match
    )
    .filter(match => 
      match.handle?.breadcrumb && stripTrailingSlash(match.pathname) !== '/'
    );

  return (
    <div
      className={cn([
        styles.breadcrumb,
        styles[`breadcrumb-${color}`]
      ])}
    >
      <Link to={buildLocalizedPath('', locale)}>
        {t('home-page')}
      </Link>
      {
        breadcrumbMatches.map((match, index) => {
          const isLast = index === breadcrumbMatches.length - 1;
          const breadcrumbTitle = match.handle?.breadcrumb;

          return (
            <span key={match.id}>
              <span className={styles.prefix}>{'// '}</span>
              {
                isLast ? (
                  <span
                    className={cn([
                      styles.active,
                      styles[`active-${color}`]
                    ])}
                  >
                    {breadcrumbTitle}
                  </span>
                ) : (
                  <Link to={withTrailingSlash(match.pathname)} reloadDocument>{breadcrumbTitle}</Link>
                )
              }
            </span>
          );
        })
      }
    </div>
  );
}

export default Breadcrumb;